import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";
import { refetchUserOnError } from "utils/refetchUserOnError";
import type { IssueComment } from "types";
import { AxiosResponse } from "axios";

type CreateCommentData = {
  issueId: string;
  content: string;
  createdBy: string;
  parent?: string | null;
  mentions?: string[];
};

const createComment = async (
  commentData: CreateCommentData
): Promise<IssueComment> => {
  const response: AxiosResponse<IssueComment> = await customAxios.post(
    `/comments/`,
    commentData
  );
  return response.data;
};

export const useCreateComment = (
  issueId: string | undefined,
  successCallback?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation<IssueComment, Error, CreateCommentData>({
    mutationFn: createComment,
    onSuccess: (newComment) => {
      if (issueId) {
        queryClient.setQueryData<IssueComment[]>(
          ["comments", issueId],
          (old) => {
            const safeComments = Array.isArray(old) ? old : [];
            return [...safeComments, newComment];
          }
        );
      }

      if (successCallback) successCallback();

      showNotification({
        title: "Comment Added",
        message: "Comment posted successfully.",
        color: "teal",
      });
    },
    onError: (error) => {
      showNotification({
        title: "Error",
        message: "Failed to post comment. Please try again.",
        color: "red",
      });

      refetchUserOnError(error, queryClient);
    },
  });
};
